import type { AllocationResult, SliderValues } from './types'

const COLORS = {
  stocks: '#FF6384',
  bonds: '#36A2EB',
  cash: '#FFCE56',
  realEstate: '#4BC0C0',
  otherCrypto: '#9966FF',
  otherAssets: '#FF9F40',
  bitcoin: '#8B5CF6'
}

const getCurrentPortfolioData = (values: SliderValues) => {
  const data = []
  
  // Map asset keys to proper display names
  const assetNameMap: { [key: string]: string } = {
    'stocks': 'Stocks',
    'bonds': 'Bonds', 
    'cash': 'Cash',
    'realEstate': 'Real Estate',
    'otherCrypto': 'Other Crypto',
    'otherAssets': 'Other Assets'
  }
  
  Object.entries(values.assets).forEach(([key, value]) => {
    if (value > 0) {
      data.push({
        label: assetNameMap[key] || key.charAt(0).toUpperCase() + key.slice(1),
        value,
        color: COLORS[key as keyof typeof COLORS] || '#ccc'
      })
    }
  })
  if (values.currentBitcoin > 0) {
    data.push({
      label: 'Bitcoin',
      value: values.currentBitcoin,
      color: COLORS.bitcoin
    })
  }
  return data
}

const normalizePortfolio = (assets: SliderValues['assets'], bitcoinValue: number, maxTotal: number = 100): { assets: SliderValues['assets'], bitcoin: number } => {
  const allValues = { ...assets, bitcoin: bitcoinValue }
  const total = Object.values(allValues).reduce((sum, val) => sum + val, 0)

  if (total <= maxTotal) {
    return { assets, bitcoin: bitcoinValue }
  }

  // If total exceeds maxTotal, scale down all values proportionally
  const scaleFactor = maxTotal / total
  const normalizedAssets = { ...assets }
  Object.keys(normalizedAssets).forEach(key => {
    normalizedAssets[key as keyof SliderValues['assets']] = Math.round(normalizedAssets[key as keyof SliderValues['assets']] * scaleFactor)
  })
  const normalizedBitcoin = Math.round(bitcoinValue * scaleFactor)

  return { assets: normalizedAssets, bitcoin: normalizedBitcoin }
}

const getIdealPortfolioData = (values: SliderValues, recommendedPercent: number) => {
  const totalNonBitcoin = Object.values(values.assets).reduce((sum, val) => sum + val, 0)
  const data = []
  
  // Map asset keys to proper display names
  const assetNameMap: { [key: string]: string } = {
    'stocks': 'Stocks',
    'bonds': 'Bonds', 
    'cash': 'Cash',
    'realEstate': 'Real Estate',
    'otherCrypto': 'Other Crypto',
    'otherAssets': 'Other Assets'
  }
  
  if (totalNonBitcoin > 0) {
    Object.entries(values.assets).forEach(([key, value]) => {
      const idealValue = (value / totalNonBitcoin) * (100 - recommendedPercent)
      if (idealValue > 0) {
        data.push({
          label: assetNameMap[key] || key.charAt(0).toUpperCase() + key.slice(1),
          value: idealValue,
          color: COLORS[key as keyof typeof COLORS] || '#ccc'
        })
      }
    })
  }
  if (recommendedPercent > 0) {
    data.push({
      label: 'Bitcoin',
      value: recommendedPercent,
      color: COLORS.bitcoin
    })
  }
  return data
}

const SLIDER_RANGES = {
  age: { min: 18, max: 80, step: 1 },
  riskTolerance: { min: 1, max: 10, step: 1 },
  assets: { min: 0, max: 100, step: 1 }, // for each asset
  income: { min: 0, max: 500000, step: 10000 },
  expenses: { min: 0, max: 500000, step: 10000 },
  horizon: { min: 1, max: 50, step: 1 },
  bitcoinConfidence: { min: 1, max: 10, step: 1 },
  currentBitcoin: { min: 0, max: 100, step: 1 },
  expectedInflation: { min: 0, max: 10, step: 0.5 },
  currentVolatility: { min: 1, max: 10, step: 1 }
}

const DEFAULT_VALUES: SliderValues = {
  age: 30,
  riskTolerance: 5,
  assets: {
    stocks: 60,
    bonds: 30,
    cash: 5,
    realEstate: 0,
    otherCrypto: 0,
    otherAssets: 5
  },
  income: 75000,
  expenses: 50000,
  horizon: 20,
  bitcoinConfidence: 7,
  currentBitcoin: 0,
  expectedInflation: 2,
  currentVolatility: 5
}

const calculateAllocation = (values: SliderValues): AllocationResult => {
  const { age, riskTolerance, assets, income, expenses, horizon, bitcoinConfidence, expectedInflation, currentVolatility, currentBitcoin } = values

  // Base calculation from age-based rule
  const maxAllocation = 110 - age
  const adjustedAllocation = maxAllocation * (riskTolerance / 10)

  // Diversification penalty for existing crypto (excluding Bitcoin)
  const existingOtherCryptoPercent = assets.otherCrypto
  const diversificationPenalty = existingOtherCryptoPercent * 0.5

  // Financial stability factor
  const expenseRatio = expenses / (income || 1) // avoid division by zero
  const stabilityFactor = Math.max(0.5, 1 - expenseRatio)

  // Horizon and confidence multipliers
  const horizonMultiplier = Math.min(2.0, horizon / 10)
  const confidenceMultiplier = bitcoinConfidence / 10
  const inflationBonus = (expectedInflation / 100) * 1.5

  // Volatility adjustment
  const volatilityFactor = 1 - ((currentVolatility - 1) / 9) * 0.5

  // Final calculation
  let recommendedPercent = (adjustedAllocation * stabilityFactor * horizonMultiplier * confidenceMultiplier * volatilityFactor) + inflationBonus - diversificationPenalty

  // Clamp to 0-100%
  recommendedPercent = Math.max(0, Math.min(100, recommendedPercent))

  // Determine allocation advice
  let allocationAdvice = ''
  if (currentBitcoin < recommendedPercent - 5) {
    allocationAdvice = 'You should consider allocating more to Bitcoin to reach the optimal level.'
  } else if (currentBitcoin > recommendedPercent + 5) {
    allocationAdvice = 'You may want to reduce your Bitcoin allocation as it exceeds the recommended level.'
  } else {
    allocationAdvice = 'Your current Bitcoin allocation is at an appropriate level.'
  }

  // Generate explanation
  const explanation = `Based on your age (${age}), risk tolerance (${riskTolerance}/10), financial stability (expense ratio: ${(expenseRatio * 100).toFixed(1)}%), investment horizon (${horizon} years), and Bitcoin confidence (${bitcoinConfidence}/10), we recommend ${recommendedPercent.toFixed(1)}% Bitcoin allocation. You currently have ${currentBitcoin}% in Bitcoin. ${allocationAdvice} This considers diversification needs and current market volatility.`

  // Confidence level
  const confidence = recommendedPercent > 50 ? 'High confidence' : recommendedPercent > 25 ? 'Moderate confidence' : 'Low confidence'

  return {
    recommendedPercent,
    confidence,
    explanation,
    allocationAdvice
  }
}

export { DEFAULT_VALUES, SLIDER_RANGES, calculateAllocation, getCurrentPortfolioData, getIdealPortfolioData, normalizePortfolio }
